import BASE_URL from '../../config/env';
import {apiClient} from '../apiClient';
import {
  IAIReviewJobGroupsResponse,
  ICreateAIReviewJobGroupPayload,
  ICreateAIReviewJobGroupResponse,
  IDeleteAIReviewJobGroupPayload,
  IUpdateAIReviewJobGroupPayload,
  IUpdateAIReviewJobGroupResponse,
  IAIReviewProtocolIntelligencePayload,
  IAIReviewProtocolIntelligenceResponse,
  IDeleteAIReviewJobGroupResponse,
  IGetAIReviewSourceTypeResponse,
  ICreateAIReviewJobPayload,
  ICreateAIReviewJobResponse,
  ISubmitAIReviewJobPayload,
  ISubmitAIReviewJobResponse,
  IGetAIReviewJobDetailsPayload,
  IGetAIReviewJobDetailsResponse,
  IUpdateAIReviewJobPayload,
  IUpdateAIReviewJobResponse,
  AIReviewCreateProtocolIntelligencePayload,
  IAIReviewJobsExportRequest,
  IExportAIReviewJobPdfPayload,
  IGetAIReviewJobResultResponse,
  IGetAIReviewJobResultPayload,
  IDeleteAIReviewJobPayload,
  IDeleteAIReviewJobResponse,
  IRetryAIReviewJobPayload,
  IRetryAIReviewJobResponse,
  IAddJobToGroupPayload,
  IAddJobToGroupResponse,
  IAIReviewEditJobNamePayload,
  IAIReviewEditJobNameResponse,
  IRemoveJobFromGroupPayload,
  IRemoveJobFromGroupResponse,
  IGetAIReviewDocErrorPanelPayload,
  IAIReviewDocErrorPanelResponse,
  IAIReviewJobResultIssueCodeListPayload,
  IAIReviewJobResultIssueCodeListResponse,
  IAIReviewJobResultRelevancyListPayload,
  IAIReviewJobResultRelevancyListResponse,
  IUpdateProtocolIntelligenceDetailsPayload,
  IGetJobListPayload,
  IGetJobListResponse,
  IGetGroundTruthEvaluationResponse,
  IDeleteAIProtocolIntelligencePayload,
  IDeleteAIProtocolIntelligenceResponse,
  IGetDownloadIssueCodingResultsResponse,
} from '../types/aiReview.type';
import {getJobDetailsMockResponse} from '../../components/ai/review/create-job/new-job-detail/GetJobDetailMockResponse';
import {getJobResultMockResponse} from '../../components/ai/review/job-results/GetJobResultMockResponse';
import {
  BackendErrorData,
  parseApiErrorWithLocalization,
} from '../../utils/custom-errors/parseApiError';
import {AxiosError} from 'axios';
import {getDocErrorPanelMockResponse} from '../../components/ai/review/job-results/job-error-panel/GetDocErrorPanelMockResponse';
import {getMockPageData} from '../../components/ai/review/MockJobs';

const aiReviewEndpoint = (path: string | number) =>
  `${BASE_URL}/ai/api/v1/review/${path}`;

export const getAIReviewJobGroups =
  async (): Promise<IAIReviewJobGroupsResponse> => {
    const {data} = await apiClient.get(aiReviewEndpoint('all_job-groups'));
    return data;
  };

export const createAIReviewJobGroup = async (
  payload: ICreateAIReviewJobGroupPayload
): Promise<ICreateAIReviewJobGroupResponse> => {
  const {data} = await apiClient.post(aiReviewEndpoint('job-groups'), payload);
  return data;
};

export const updateAIReviewJobGroup = async (
  payload: IUpdateAIReviewJobGroupPayload
): Promise<IUpdateAIReviewJobGroupResponse> => {
  const {jobGroupId, name} = payload;
  const {data} = await apiClient.put(
    aiReviewEndpoint(`job-groups/${jobGroupId}`),
    {name}
  );
  return data;
};

export const deleteAIReviewJobGroup = async (
  payload: IDeleteAIReviewJobGroupPayload
): Promise<IDeleteAIReviewJobGroupResponse> => {
  const {jobGroupId} = payload;
  const {data} = await apiClient.delete(
    aiReviewEndpoint(`job-groups?jobGroupId=${jobGroupId}`)
  );
  return data;
};

// ================Need to remove all Try Catch and mock response once API is ready================
export const getAllProtocolIntelligenceIds = async (): Promise<
  IAIReviewProtocolIntelligencePayload[]
> => {
  const {data = []} = await apiClient.get(
    aiReviewEndpoint(`protocol-intelligence/getids`)
  );
  return data;
};

export const getProtocolIntelligenceDetails = async (
  payload: IAIReviewProtocolIntelligencePayload
): Promise<IAIReviewProtocolIntelligenceResponse> => {
  const {protocolId} = payload;
  const {data = null} = await apiClient.get(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}`)
  );
  return data;
};

export const createProtocolIntelligence = async (
  payload: AIReviewCreateProtocolIntelligencePayload
) => {
  let requestData: any = payload;
  let headers = {};
  const hasFile = Object.values(payload).some((value) => value instanceof File);
  if (hasFile) {
    const formData = new FormData();
    Object.entries(payload).forEach(([key, value]) => {
      if (value !== undefined && value !== null) {
        formData.append(key, value as any);
      }
    });
    requestData = formData;
    headers = {
      'Content-Type': 'multipart/form-data',
    };
  }
  const {data} = await apiClient.post<IAIReviewProtocolIntelligenceResponse>(
    aiReviewEndpoint('protocol-intelligence'),
    requestData,
    {headers}
  );
  return data;
};

export const approveProtocolIntelligence = async ({
  protocolId = '',
}: {
  protocolId: string;
}) => {
  const {data} = await apiClient.post(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}/approve`)
  );
  return data;
};

export const updateProtocolIntelligenceDetails = async (
  payload: IUpdateProtocolIntelligenceDetailsPayload
) => {
  const {protocolId, ...rest} = payload;
  // Remove 'id' field before sending to API
  const body = {
    ...rest,
    issueCodes: rest.issueCodes.map(({question, code}) => ({code, question})),
  };
  const {data} = await apiClient.put(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}`),
    body
  );
  return data;
};

export const getAIReviewSourceType =
  async (): Promise<IGetAIReviewSourceTypeResponse> => {
    try {
      return await apiClient.get(aiReviewEndpoint('sourceTypes'));
    } catch (error) {
      console.warn('Unable to get source type', error);
    }
    return {id: 0, sourceType: 'IssueCode'}; //Returns mock response
  };

export const createAIReviewJob = async (
  payload: ICreateAIReviewJobPayload
): Promise<ICreateAIReviewJobResponse> => {
  const {data} = await apiClient.post(aiReviewEndpoint('jobs'), payload);
  return data;
};

export const updateAIReviewJob = async (
  payload: IUpdateAIReviewJobPayload
): Promise<IUpdateAIReviewJobResponse> => {
  const {jobId, ...body} = payload;
  const {data} = await apiClient.put(aiReviewEndpoint(`jobs/${jobId}`), body);
  return data;
};

export const submitAIReviewJob = async (
  payload: ISubmitAIReviewJobPayload
): Promise<ISubmitAIReviewJobResponse> => {
  const {data} = await apiClient.post(aiReviewEndpoint('jobs/submit'), payload);
  return data;
};

export const updateAIReviewJobName = async (
  payload: IAIReviewEditJobNamePayload
): Promise<IAIReviewEditJobNameResponse> => {
  const {jobId, ...body} = payload;
  try {
    const {data} = await apiClient.put(aiReviewEndpoint(`jobs/${jobId}`), body);
    return data;
  } catch (error) {
    console.warn('Unable to update Job Name', error);
  }
  return {
    jobId: '1',
    name: 'Updated Job Name',
    updatedBy: 'currentUser',
    updatedAt: '2026-04-10T10:15:30Z',
  };
};

export const getAIReviewJobDetails = async (
  payload: IGetAIReviewJobDetailsPayload
): Promise<IGetAIReviewJobDetailsResponse> => {
  const {jobId} = payload;
  try {
    const {data} = await apiClient.get(aiReviewEndpoint(`jobs/${jobId}`));
    return data;
  } catch (error) {
    console.warn('API failed – using mock response', error);
  }

  return getJobDetailsMockResponse;
};

export const exportAIReviewJobs = async (
  payload: IAIReviewJobsExportRequest
): Promise<Blob> => {
  const response = await apiClient.post(
    aiReviewEndpoint('api/jobs/export'),
    payload,
    {
      responseType: 'arraybuffer',
    }
  );
  return new Blob([response.data], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
};

export const getAIReviewJobDetailData = async (
  payload: IExportAIReviewJobPdfPayload
): Promise<IGetAIReviewJobDetailsResponse> => {
  const {jobId} = payload;
  const {data} = await apiClient.get(aiReviewEndpoint(`jobs/${jobId}`));

  return data;
};

export const deleteAIReviewJob = async (
  payload: IDeleteAIReviewJobPayload
): Promise<IDeleteAIReviewJobResponse> => {
  const {jobId} = payload;
  const {data} = await apiClient.delete(aiReviewEndpoint(`jobs/${jobId}`));
  return data;
};

export const retryAIReviewJob = async (
  payload: IRetryAIReviewJobPayload
): Promise<IRetryAIReviewJobResponse> => {
  const {jobId} = payload;
  const {data} = await apiClient.post(aiReviewEndpoint(`jobs/${jobId}/retry`));
  return data;
};

export const addJobToGroup = async (
  payload: IAddJobToGroupPayload
): Promise<IAddJobToGroupResponse> => {
  const {groupId, jobId, addedBy, reason} = payload;
  const {data} = await apiClient.post(
    aiReviewEndpoint(`job-groups/${groupId}/jobs`),
    {
      jobId,
      ...(addedBy ? {addedBy} : {}),
      ...(reason ? {reason} : {}),
    }
  );
  return data;
};

export const removeJobFromGroup = async (
  payload: IRemoveJobFromGroupPayload
): Promise<IRemoveJobFromGroupResponse> => {
  const {groupId, jobId} = payload;
  const {data} = await apiClient.delete(
    aiReviewEndpoint(`job-groups/${groupId}/jobs/${jobId}`)
  );
  return data;
};

export const getAIReviewJobResult = async (
  payload: IGetAIReviewJobResultPayload
): Promise<IGetAIReviewJobResultResponse> => {
  const {jobId} = payload;

  try {
    const {data} = await apiClient.get(
      aiReviewEndpoint(`jobs/${jobId}/results`)
    );
    return data;
  } catch (error) {
    console.warn('API failed – using mock response', error);
  }

  return getJobResultMockResponse;
};

export const getAIReviewDocErrorPanelList = async (
  payload: IGetAIReviewDocErrorPanelPayload
): Promise<IAIReviewDocErrorPanelResponse> => {
  try {
    return await apiClient.post(
      aiReviewEndpoint(`docs/errored/search`),
      payload
    );
  } catch (error) {
    console.warn('API failed – using mock response', error);
  }
  const filteredItems = getDocErrorPanelMockResponse.items.filter((item) =>
    item.errorMessage
      .toLowerCase()
      .includes(payload.search?.toLowerCase() || '')
  );
  return {
    items: filteredItems,
    total: filteredItems.length,
  };
};

export const getIssueCodingResultsDownload = async (
  jobId: number | string
): Promise<IGetDownloadIssueCodingResultsResponse> => {
  try {
    const response = await apiClient.post(
      aiReviewEndpoint(`jobs/${jobId}/download`),
      {
        responseType: 'blob',
        headers: {
          Accept:
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        },
      }
    );

    const disposition = response.headers['content-disposition'];

    const match = disposition?.match(/filename\*=UTF-8''(.+)$/);

    const filename = match
      ? decodeURIComponent(match[1])
      : `issue-coding-results-${jobId}.xlsx`;

    return {
      blob: response.data,
      filename,
      contentDisposition: disposition,
    };
  } catch (error) {
    throw parseApiErrorWithLocalization(
      error as AxiosError<BackendErrorData>,
      'docViewerPanel'
    );
  }
};

export const getAIReviewJobResultIssueCodeList = async (
  jobId: number | string,
  payload: IAIReviewJobResultIssueCodeListPayload
): Promise<IAIReviewJobResultIssueCodeListResponse> => {
  // jobs/${jobId}/results/issue-codes
  const {data} = await apiClient.post(
    aiReviewEndpoint(`jobs/2/results/issue-codes`), // passing hardcoded jobID as 2, need to replace with dynamic jobId in future/
    payload
  );
  return data;
};

export const getAIReviewJobResultRelevancyList = async (
  jobId: number | string,
  payload: IAIReviewJobResultRelevancyListPayload
): Promise<IAIReviewJobResultRelevancyListResponse> => {
  // jobs/${jobId}/results/relevancy
  const {data} = await apiClient.post(
    aiReviewEndpoint(`jobs/2/results/relevancy`), // passing hardcoded jobID as 2, need to replace with dynamic jobId in future/
    payload
  );
  return data;
};

export const getJobList = async (
  payload: IGetJobListPayload
): Promise<IGetJobListResponse> => {
  //---Remove this when api is done
  const mockData = getMockPageData(
    payload.pageNumber,
    payload.displayLength,
    {field: payload.sortingColumn, sort: payload.direction},
    payload.columnFilter
  );
  //-------
  try {
    const {data} = await apiClient.post(aiReviewEndpoint('jobs/list'), payload);

    //--remove when api is ready
    return {
      ...data,
      jobs: [...data.jobs, ...mockData.jobs],
    };
    //---

    // return data; //uncomment when api is ready
  } catch (error) {
    console.warn('Jobs API failed', error);
  }

  return mockData; //--remove when api is ready
};

export const getGroundTruthEvaluation = async (
  jobId: string
): Promise<IGetGroundTruthEvaluationResponse> => {
  try {
    return await apiClient.get(
      aiReviewEndpoint(`jobs/${jobId}/ground-truth-evaluation`)
    );
  } catch (error) {
    console.warn('Unable to get ground truth', error);
  }
  return {
    jobId: '987e6543-e21b-12d3-a456-426614174999',
    groundTruthName: 'Senior Attorney Review Set - January 2024',
    groundTruthDocCount: 2000,
    confusionMatrix: {
      truePositives: 320,
      falsePositives: 80,
      trueNegatives: 1520,
      falseNegatives: 80,
    },
    metrics: {
      accuracy: 0.92,
      precision: 0.8,
      recall: 0.8,
      f1Score: 0.8,
    },
  };
};

export const deleteAIProtocolIntelligence = async (
  payload: IDeleteAIProtocolIntelligencePayload
): Promise<IDeleteAIProtocolIntelligenceResponse> => {
  const {protocolId} = payload;
  const {data} = await apiClient.delete(
    aiReviewEndpoint(`protocol-intelligence/${protocolId}`)
  );
  return data;
};
export const getProtocolDownload = async (
  protocolId: string | number | null
): Promise<{
  isSuccess: boolean;
  data?: Blob;
  headers?: any;
  error?: unknown;
}> => {
  try {
    const response = await apiClient.get(
      aiReviewEndpoint(`protocol-intelligence/${protocolId}/download`),
      {responseType: 'blob'}
    );
    return {isSuccess: true, data: response.data, headers: response.headers};
  } catch (error) {
    return {isSuccess: false, error};
  }
};
